/**
 * A span that has come down (ROUND4 §1, mission 13). When the last pier under a bridge is blown the
 * deck goes with it, and the sim puts a low run of rubble across the lane that nobody crosses
 * (`hazards/spans.ts` `spanWrecks`). Until now nothing drew it: the deck simply vanished and the
 * player walked into a wall in mid-air over a river he could see straight through.
 *
 * Built in the bridge's own frame, exactly as `bridgeRun` is: along local x, `length` metres, y = 0
 * at the BANK. What is left is the stub of deck still on each abutment and, between them, the broken
 * sections of the span canted into whatever is underneath - `site` says where that is and whether
 * things sink into it, so one model reads on open water, on ice and on dry ground.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import { MeshBuilder, place } from './MeshBuilder';
import { type Point, strut } from './parts';
import { BRIDGE_DECK_THICKNESS, BRIDGE_DECK_Y, BRIDGE_HALF_WIDTH } from './pipeline';

export interface SpanSite {
  /** Where the water, ice or ground under the deck lies, in the bridge's own frame (below its y = 0). */
  surfaceY: number;
  /** Open water: what reaches it goes under. Ice or dry ground: it lies broken on top, and all of it shows. */
  submerged: boolean;
}

/** The two sites, at the height `render/world/river` puts a river surface: what the turntable shows. */
export const SPAN_IN_WATER: SpanSite = { surfaceY: -0.3, submerged: true };
export const SPAN_ON_ICE: SpanSite = { surfaceY: -0.3, submerged: false };

/**
 * Sections of the fallen deck: `at` is a fraction of the half-length, `w` of the whole length, `rise`
 * how far a section floats above the surface when there is water to float in, and `roll` how steeply
 * it is canted. One up on edge, one almost flat, one tipped the other way - "it let go", at a glance.
 */
const PIECES = [
  { at: -0.46, w: 0.30, roll: 0.62, yaw: 0.11, rise: 0.45 },
  { at: -0.04, w: 0.26, roll: 0.18, yaw: -0.21, rise: 0.08 },
  { at: 0.41, w: 0.28, roll: -0.50, yaw: 0.07, rise: 0.34 },
] as const;

/** Beams out of the wreckage: `at` along the half-length, a z, then a length, a lean and a heading. */
const BEAMS = [
  { at: -0.2, z: -1.5, len: 2.6, lean: 0.8, yaw: 0.4 },
  { at: 0.12, z: 1.2, len: 2.2, lean: 0.55, yaw: -0.7 },
  { at: 0.62, z: -0.4, len: 2.0, lean: 1.0, yaw: 0.2 },
] as const;

/** Lowest point of a deck slab `w` long and canted by `roll`, below its own centre. */
function sag(w: number, roll: number): number {
  return (w / 2) * Math.abs(Math.sin(roll)) + (BRIDGE_DECK_THICKNESS / 2) * Math.abs(Math.cos(roll));
}

/** The stub of deck left on one abutment, sagging toward the gap, with its rail and a snapped post. */
function stubEnd(mb: MeshBuilder, p: Palette, sx: number, half: number, stub: number, surfaceY: number): void {
  const halfW = BRIDGE_HALF_WIDTH;
  const deck = { all: p.color('timber.lo'), top: p.color('timber.mid'), front: p.color('ink.1') };
  const broken = { all: p.color('ink.1'), top: p.color('timber.lo') };
  const x = sx * (half - stub / 2);
  const under = BRIDGE_DECK_Y - BRIDGE_DECK_THICKNESS;
  mb.box(stub, BRIDGE_DECK_THICKNESS, halfW * 2, deck, place(x, under, 0, 0, 1, 0, sx * 0.17));
  // The abutment it still rests on, carried down past the surface so it is founded, not floating.
  const foot = surfaceY - 0.5;
  mb.box(0.6, under - foot, halfW * 2 - 0.5, p.color('timber.lo'), place(sx * (half - 0.3), foot, 0));
  for (const sz of [-1, 1]) {
    const z = sz * (halfW - 0.1);
    mb.box(stub * 0.8, 0.1, 0.14, p.color('timber.hi'), place(x, BRIDGE_DECK_Y + 0.6, z, 0, 1, 0, sx * 0.17));
    mb.box(0.16, 0.62, 0.16, p.color('timber.lo'), place(sx * (half - 0.22), BRIDGE_DECK_Y, z));
    mb.box(0.16, 0.5, 0.16, broken, place(x - sx * stub * 0.4, BRIDGE_DECK_Y - 0.12, z, 0, 1, 0, sx * 0.55));
  }
}

/**
 * The broken span. `length` is the bridge's own, so the wreck fills the footprint the deck did - and
 * the lane the sim blocks. Nothing here is walkable: the road stops at the abutments.
 */
export function brokenSpan(p: Palette, rng: Pcg32, length = 12, site: SpanSite = SPAN_IN_WATER): MeshBuilder {
  const mb = new MeshBuilder();
  const half = length / 2;
  const halfW = BRIDGE_HALF_WIDTH;
  const deck = { all: p.color('timber.lo'), top: p.color('timber.mid'), front: p.color('ink.1') };
  const broken = { all: p.color('ink.1'), top: p.color('timber.lo') };
  const stub = Math.min(2.4, half * 0.34);
  for (const sx of [-1, 1]) stubEnd(mb, p, sx, half, stub, site.surfaceY);

  /* ---- what fell: sections of deck canted into the gap, and the beams that carried them */
  for (const piece of PIECES) {
    // Capped: on a long span a section scaled all the way up stands too high out of the water.
    const w = Math.min(3.6, Math.max(1.6, piece.w * length));
    // In water a section floats where it settled; on ice or dry ground it simply lies on the surface.
    const y = site.submerged ? site.surfaceY + piece.rise : site.surfaceY + sag(w, piece.roll) - 0.08;
    mb.with(place(piece.at * half, y, rng.range(-0.35, 0.35), piece.yaw, 1, 0, piece.roll), () => {
      mb.box(w, BRIDGE_DECK_THICKNESS, halfW * 1.7, deck, undefined, true);
      // The rail still on one edge of the section: what tells you this black timber was a road.
      mb.box(w * 0.75, 0.1, 0.14, p.color('timber.hi'), place(0, 0.62, halfW * 0.8));
      for (const dx of [-0.3, 0.3]) mb.box(0.16, 0.56, 0.16, broken, place(dx * w, 0.02, halfW * 0.8, 0, 1, 0, 0.1));
    });
  }
  const footY = site.surfaceY - (site.submerged ? 0.15 : 0.05);
  for (const beam of BEAMS) {
    const x = beam.at * half;
    const reach = beam.len * Math.cos(beam.lean);
    const from: Point = [x, footY, beam.z];
    const to: Point = [x + Math.sin(beam.yaw) * reach, footY + beam.len * Math.sin(beam.lean), beam.z + Math.cos(beam.yaw) * reach];
    strut(mb, from, to, 0.22, p.color('timber.lo'));
  }
  // A length of handrail adrift: the one pale line in all that dark timber, flat on the surface.
  mb.box(Math.min(3.4, length * 0.3), 0.1, 0.14, p.color('timber.hi'),
    place(-half * 0.2, site.surfaceY + 0.04, halfW * 0.55, rng.range(0.2, 0.5)));
  return mb;
}
